import { supabase } from './supabase'
import { notifyExternally } from '../context/ToastContext'
import { refreshSessionOnce } from './sessionRefresh'

// Client-side chokepoint for the four auth-rejection states the server
// returns: SESSION_INVALIDATED, BANNED, FROZEN, SUSPENDED, plus bare 401.
// Each state has its own handler with a state-appropriate redirect; all
// share one module-level _firing flag (winner-takes-all). The first
// handler to fire wins; subsequent 401/403s within the page-teardown
// window no-op. Prevents a burst of concurrent stale-session requests
// from spawning multiple toasts, multiple signOuts, multiple redirects.
//
// Each handler also short-circuits if the user is already on the
// destination path. Without this, mount-time authenticated requests on
// the destination (e.g. AuthContext.fetchProfile firing on / for a frozen
// user before SuspensionGate has mounted) trigger window.location.reload()
// in a tight loop — the module re-evaluates on each reload and _firing
// resets to false. The chokepoint's job is to GET the user to the
// destination; once there, downstream gates (SuspensionGate, /login
// banner) own what they see.
let _firing = false

// AUTH_TRANSIENT cooldown timestamp. notifyExternally does NOT dedupe
// (see ToastContext.jsx:9-23); without this cooldown, a burst of 5
// failed parallel requests during a GoTrue wobble would stack 5 toasts.
// Deliberately separate from _firing — multiple transient toasts
// across a sustained outage are acceptable signal; multiple redirect
// navigations are destructive.
let _lastTransientToastAt = 0
const TRANSIENT_TOAST_COOLDOWN_MS = 2_000

// Clear all Supabase-managed storage. Byte-equivalent to the storage-clear
// portion of AuthContext.signOut()'s hard-logout fallback (lines 184-191).
// Extracted so non-React callers (axios interceptors, fetch error handlers)
// can use it without going through useAuth(). Does NOT touch React state
// or trigger navigation — callers do those separately.
export function forceLocalSignOut() {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('sb-')) localStorage.removeItem(key)
    }
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('sb-')) sessionStorage.removeItem(key)
    }
  } catch {}
}

// 401 + code='SESSION_INVALIDATED'. Stale-iat JWT after password rotation.
// Show a toast, wait long enough to read it, signOut, redirect to /login.
// Behavior preserved from the prior sessionInvalidatedRedirect.js module
// (2026-05-11 merge) plus the pathname guard.
export async function handleSessionInvalidated() {
  if (typeof window !== 'undefined' && window.location.pathname === '/login') return
  if (_firing) return
  _firing = true
  notifyExternally(
    'Your password was changed. Please log out and log back in with your new password to continue.',
    'success',
    5000
  )
  await new Promise(r => setTimeout(r, 5000))
  try { await supabase.auth.signOut({ scope: 'local' }) } catch {}
  if (typeof window !== 'undefined') {
    window.location.assign('/login?reason=session_invalidated')
  }
}

// 403 + code='BANNED'. Account banned. Sign out + redirect to /login with
// banned banner. supabase.auth.signOut() can fail when banned_until=9999-12-31
// is set (GoTrue rejects the token exchange); forceLocalSignOut clears the
// Supabase-managed storage so the SDK can't resurrect the session.
export async function handleBanned() {
  if (typeof window !== 'undefined' && window.location.pathname === '/login') return
  if (_firing) return
  _firing = true
  try { await supabase.auth.signOut({ scope: 'local' }) } catch {}
  forceLocalSignOut()
  if (typeof window !== 'undefined') {
    window.location.assign('/login?reason=banned')
  }
}

// 403 + code='FROZEN'. CSAM freeze. Navigate to /; SuspensionGate mounts,
// fetches /api/user/suspension-status, sees the row written by the
// manual-freeze or scanner-auto path, and renders.
// DOES NOT signOut — the gate requires an authenticated session to call
// /api/user/suspension-status. Signing out breaks the gate. Asymmetric with
// handleBanned and handleBareAuth on purpose.
export function handleFrozen() {
  if (typeof window !== 'undefined' && window.location.pathname === '/') return
  if (_firing) return
  _firing = true
  if (typeof window === 'undefined') return
  window.location.assign('/')
}

// 403 + code='SUSPENDED'. Timed suspension. Same handling as FROZEN —
// SuspensionGate renders the suspension UI and shows the expires_at
// countdown from the suspensions row. DOES NOT signOut for the same
// reason as handleFrozen.
export function handleSuspended() {
  if (typeof window !== 'undefined' && window.location.pathname === '/') return
  if (_firing) return
  _firing = true
  if (typeof window === 'undefined') return
  window.location.assign('/')
}

// 403 + code='PENDING_DELETION'. Soft-deleted account; user signed back in.
// Navigate to /account/restore with the timestamp so the page can render
// the countdown without a separate fetch. DOES NOT signOut — the restore
// endpoint requires an authenticated session. Asymmetric with handleBanned
// for the same reason handleFrozen is.
export function handlePendingDeletion(pendingDeletionAt) {
  if (typeof window !== 'undefined' && window.location.pathname === '/account/restore') return
  if (_firing) return
  _firing = true
  if (typeof window === 'undefined') return
  const qs = pendingDeletionAt
    ? '?pendingDeletionAt=' + encodeURIComponent(pendingDeletionAt)
    : ''
  window.location.assign('/account/restore' + qs)
}

// LEGACY FALLBACK: 401 with no `code` field. Should not fire after Phase 2
// classifier ships — any server route returning bare 401 indicates an
// unmigrated handler or a defensive escape hatch. Behavior matches
// handleAuthInvalid (signOut + session_expired redirect) for safety.
// Phase 3 of auth chokepoint rework split the original handleBareAuth
// into NO_AUTH (anon — no signOut), AUTH_INVALID (token went bad —
// signOut), and AUTH_TRANSIENT (infra wobble — no signOut, no redirect)
// per docs/plans/2026-05-25-auth-chokepoint-rework.md §6.
export async function handleBareAuth() {
  if (typeof window !== 'undefined' && window.location.pathname === '/login') return
  if (_firing) return
  _firing = true
  try { await supabase.auth.signOut({ scope: 'local' }) } catch {}
  forceLocalSignOut()
  if (typeof window !== 'undefined') {
    window.location.assign('/login?reason=session_expired')
  }
}

// Shared helper for native fetch sites: inspect a Response, classify by
// status + code, and delegate to dispatchAuthError. Returns void; caller
// is responsible for treating !response.ok as an error after this returns.
// The dispatched handler may trigger a redirect that never returns (page
// navigates away). EMAIL_NOT_VERIFIED and other unknown codes pass through
// via dispatchAuthError's silent-no-op default case.
//
// Phase 3 of auth chokepoint rework: this used to carry an inline branch
// table identical to the axios interceptor sites in AuthContext.jsx and
// lib/api.js. All three now delegate to dispatchAuthError below — single
// source of truth for (status, code) → handler routing.
//
// Status filter includes 503 so AUTH_TRANSIENT can dispatch via the
// native-fetch sites (fetchProfile in AuthContext, OnlineStatusContext
// heartbeat + privacy preload). Phase 2 server returns 503 for
// AUTH_TRANSIENT; without 503 in the filter, transient errors from those
// sites would slip through silently.
export async function handleAuthError(response) {
  if (!response || response.ok) return
  const status = response.status
  if (status !== 401 && status !== 403 && status !== 503) return
  let code = null
  let body = null
  try {
    body = await response.clone().json()
    code = body?.code ?? null
  } catch {}
  dispatchAuthError(status, code, body)
}

// Native-fetch counterpart to the axios refresh-and-retry interceptors
// (sessionRefresh.js tryRefreshAndReplay). Issues the fetch; on a 401
// AUTH_INVALID, refreshes once (shared single-flight) and replays the request
// ONCE with the fresh token. Any other outcome — success, non-401,
// non-AUTH_INVALID, refresh failure, or an again-401 replay — returns the final
// Response unchanged so the caller's existing handleAuthError(res) still runs
// (logout on a genuine AUTH_INVALID). At most one refresh+replay per call;
// cannot loop. Used by AuthContext.fetchProfile + OnlineStatusContext.
export async function fetchWithAuthRetry(input, init = {}) {
  const res = await fetch(input, init)
  if (res.ok || res.status !== 401) return res
  let code = null
  try { code = (await res.clone().json())?.code ?? null } catch {}
  if (code !== 'AUTH_INVALID') return res
  const token = await refreshSessionOnce()
  if (!token) return res
  const headers = { ...(init.headers ?? {}), Authorization: `Bearer ${token}` }
  return fetch(input, { ...init, headers })
}

// 401 + code='NO_AUTH'. Anon user hit an authenticated route — no
// session to sign out, no token to invalidate. Redirect with
// `login_required` reason so LoginPage shows a "Sign in to continue"
// banner instead of "Your session expired" (which is misleading for
// a user who was never signed in). Phase 3 of auth chokepoint rework.
export function handleNoAuth() {
  if (typeof window !== 'undefined' && window.location.pathname === '/login') return
  if (_firing) return
  _firing = true
  if (typeof window === 'undefined') return
  window.location.assign('/login?reason=login_required')
}

// 401 + code='AUTH_INVALID'. Token present but rejected by GoTrue as
// permanently invalid (bad_jwt, AuthSessionMissingError post password
// rotation, expired refresh window). Signed-in session went bad —
// signOut to clear the dead token, redirect with session_expired so
// LoginPage shows the right banner. Behavior matches the old
// handleBareAuth for the in-the-wild case (user was signed in, token
// went bad mid-session); the split is purely so the NEW NO_AUTH case
// can get a different reason string. Phase 3 of auth chokepoint rework.
export async function handleAuthInvalid() {
  if (typeof window !== 'undefined' && window.location.pathname === '/login') return
  if (_firing) return
  _firing = true
  try { await supabase.auth.signOut({ scope: 'local' }) } catch {}
  forceLocalSignOut()
  if (typeof window !== 'undefined') {
    window.location.assign('/login?reason=session_expired')
  }
}

// 503 + code='AUTH_TRANSIENT'. GoTrue temporarily unreachable —
// session presumed valid, failure is infrastructure. NO signOut,
// NO redirect; just a toast so the user knows something's wrong and
// retry made sense.
//
// 2-second cooldown via _lastTransientToastAt, NOT _firing — multiple
// transient toasts across the page lifecycle ARE acceptable signal
// (every 2s an infra hiccup is real news); a burst stack of N toasts
// from one fan-out of parallel API calls is not. _firing is reserved
// for redirect handlers where duplicate redirects would be destructive.
//
// notifyExternally does NOT dedupe (see ToastContext.jsx:9-23); the
// cooldown here is the only suppression layer.
// Phase 3 of auth chokepoint rework.
export function handleAuthTransient() {
  const now = Date.now()
  if (now - _lastTransientToastAt < TRANSIENT_TOAST_COOLDOWN_MS) return
  _lastTransientToastAt = now
  notifyExternally(
    'Connection issue — retrying. If this persists, refresh the page.',
    'warning',
    4000
  )
}

// Single dispatcher replacing the three byte-identical branch tables
// previously at AuthContext.jsx, lib/api.js, and handleAuthError above.
// All three call sites now delegate here so the (status, code) →
// handler mapping has a single source of truth.
//
// Wire contract from Phase 2 server-side classifier (commit d40c349):
//   401 NO_AUTH            — no Authorization header, anon hit auth route
//   401 AUTH_INVALID       — token rejected by GoTrue (permanent failure)
//   503 AUTH_TRANSIENT     — GoTrue temporarily unreachable (recoverable)
//   401 SESSION_INVALIDATED — iat < password_rotated_at
//   403 BANNED / FROZEN / SUSPENDED / PENDING_DELETION — state codes
//   401 no code            — legacy fallback (should not fire post-Phase-2)
//
// Default case: silent no-op. Unknown (status, code) combinations
// fall through with no side effect. Callers ALWAYS re-reject the
// original error after dispatchAuthError returns so per-form .catch()
// handlers still fire (preserves inline-form-error UX).
export function dispatchAuthError(status, code, data) {
  if (status === 401 && code === 'SESSION_INVALIDATED') return handleSessionInvalidated()
  if (status === 401 && code === 'NO_AUTH')             return handleNoAuth()
  if (status === 401 && code === 'AUTH_INVALID')        return handleAuthInvalid()
  if (status === 503 && code === 'AUTH_TRANSIENT')      return handleAuthTransient()
  if (status === 403 && code === 'BANNED')              return handleBanned()
  if (status === 403 && code === 'FROZEN')              return handleFrozen()
  if (status === 403 && code === 'PENDING_DELETION')    return handlePendingDeletion(data?.pendingDeletionAt)
  if (status === 403 && code === 'SUSPENDED')           return handleSuspended()
  if (status === 401 && !code)                          return handleBareAuth()
  // No-op default — unknown shapes fall through. Caller re-rejects.
}
